/* eslint-disable */
import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Telescope, RefreshCw, Clock, AlertTriangle, ChevronRight, Loader2, Pencil, Trash2, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import AssignWithBriefModal from "@/components/AssignWithBriefModal";
import CompletedHistoryPanel from "@/components/CompletedHistoryPanel";
import CloseCardModal from "@/components/CloseCardModal";
import { API } from "@/lib/constants";

const LANES = [
  { id: "triage",             label: "Triage",             tint: "bg-slate-50 border-slate-200",   header: "bg-slate-100 text-slate-800" },
  { id: "researching",        label: "Researching",        tint: "bg-sky-50 border-sky-200",       header: "bg-sky-100 text-sky-900" },
  { id: "research_completed", label: "Research Completed", tint: "bg-cyan-50 border-cyan-200",     header: "bg-cyan-100 text-cyan-900" },
  { id: "drafting_brief",     label: "Drafting Brief",     tint: "bg-indigo-50 border-indigo-200", header: "bg-indigo-100 text-indigo-900" },
  { id: "review",             label: "Review",             tint: "bg-amber-50 border-amber-200",   header: "bg-amber-100 text-amber-900" },
  { id: "approved",           label: "Approved",           tint: "bg-emerald-50 border-emerald-200", header: "bg-emerald-100 text-emerald-900" },
  { id: "campaign_sent",      label: "Campaign Sent",      tint: "bg-teal-50 border-teal-200",     header: "bg-teal-100 text-teal-900" },
  { id: "rejected",           label: "Rejected",           tint: "bg-rose-50 border-rose-200",     header: "bg-rose-100 text-rose-900" },
  { id: "completed",          label: "Completed",          tint: "bg-stone-50 border-stone-200",   header: "bg-stone-100 text-stone-800" },
];

const SLA_TONE = {
  green:    "bg-emerald-100 text-emerald-800 border-emerald-200",
  amber:    "bg-amber-100 text-amber-800 border-amber-200",
  red:      "bg-rose-100 text-rose-800 border-rose-200",
  breached: "bg-rose-600 text-white border-rose-700",
  unknown:  "bg-slate-100 text-slate-700 border-slate-200",
};

const TRIAGE_LABELS = {
  research_crm: "Research CRM",
  add_to_pipeline: "Pipeline",
  conversation_entry: "Conversation",
  authority_content: "Authority",
  track_only: "Track",
  brief_team: "Brief team",
};

function fmtRemaining(h) {
  if (h == null) return "—";
  const abs = Math.abs(h);
  if (abs >= 24) return `${Math.round(abs / 24 * 10) / 10}d`;
  return `${Math.round(abs * 10) / 10}h`;
}

function CardChip({ task, onOpen, onDragStart }) {
  const tone = SLA_TONE[task.sla_label] || SLA_TONE.unknown;
  const triage = TRIAGE_LABELS[task.triage_action] || task.triage_action || "task";
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onOpen}
      className="cursor-grab active:cursor-grabbing bg-white rounded-md border border-slate-200 px-3 py-2 shadow-sm hover:shadow transition mb-2 min-w-[220px] max-w-[280px]"
      data-testid={`lens-card-${task.task_id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{triage}</span>
        <Badge className={`text-[10px] border ${tone}`}>
          {task.sla_breached ? "BREACHED " : ""}{fmtRemaining(task.sla_hours_remaining)}
        </Badge>
      </div>
      <p className="text-sm font-medium text-slate-900 line-clamp-2 leading-tight">{task.title}</p>
      <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
        <span>{task.assignee_email ? task.assignee_email.split("@")[0] : "Unassigned"}</span>
        {task.error && (
          <span className="inline-flex items-center gap-1 text-rose-600 font-medium" title={task.error}>
            <AlertTriangle className="h-3 w-3" /> auto-fire
          </span>
        )}
      </div>
    </div>
  );
}

export default function LensBoard({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [opened, setOpened] = useState(null);
  const [filter, setFilter] = useState("all"); // all | mine | breached
  // REX-COMPLETED-HISTORY (May 2026) — toggle between Kanban work board
  // and the Completed/Rejected archive view ("show me what shipped or
  // got killed over time").
  const [view, setView] = useState("kanban"); // kanban | completed
  // REX-BRIEF (May 2026) — modal state for "Assign with brief" flow.
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  // REX-LENS-CLOSE (Jun 2026) — structured close-with-outcome modal.
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  // Inline brief-edit textarea state on the opened card.
  const [briefEditing, setBriefEditing] = useState(false);
  const [briefDraft, setBriefDraft] = useState("");
  // REX-USER-ROLES (May 2026) — fetch the analyst pool so admins can pick an
  // assignee directly from the card modal. Non-admins can still self-claim
  // via the existing Claim button.
  const [analystPool, setAnalystPool] = useState([]);
  // G2 — analyst output on the card: findings, attachments, resolved client, and the
  // H1 "Give to…" hand-off (real-staff-only pool).
  const [findingsDraft, setFindingsDraft] = useState("");
  const [findingsEditing, setFindingsEditing] = useState(false);
  const [savingFindings, setSavingFindings] = useState(false);
  const [taskClient, setTaskClient] = useState(null); // { has_client, client_id, client_name }
  const [attaching, setAttaching] = useState(false);
  const [giveModalOpen, setGiveModalOpen] = useState(false);
  const [staffPool, setStaffPool] = useState([]);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const canSelfClaim = isAdmin || !!user?.is_analyst;

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/scrum/v2/assignable-staff`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { staff: [] }))
      .then((d) => { if (!cancelled) setStaffPool(d.staff || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/analyst-tasks/analysts`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { analysts: [] }))
      .then((d) => { if (!cancelled) setAnalystPool(d.analysts || []); })
      .catch(() => { /* silently */ });
    return () => { cancelled = true; };
  }, []);

  // G2 — when a card opens, resolve its client (to decide attach vs "no client")
  // and reset the findings editor.
  useEffect(() => {
    if (!opened?.task_id) { setTaskClient(null); setFindingsEditing(false); return; }
    setFindingsEditing(false);
    setFindingsDraft(opened.findings || "");
    let cancelled = false;
    fetch(`${API}/analyst-tasks/${opened.task_id}/client`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setTaskClient(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [opened?.task_id]);

  const saveFindings = useCallback(async () => {
    const text = findingsDraft.trim();
    if (!text) { toast.error("Write your findings first."); return; }
    setSavingFindings(true);
    try {
      const r = await fetch(`${API}/analyst-tasks/${opened.task_id}/findings`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ findings: text }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(b.detail || "Could not save findings"); return; }
      const landed = b.client?.status === "applied";
      toast.success(landed ? `Findings saved — also filed on ${b.client.client_name || "the client"}` : "Findings saved on the card");
      setOpened(b.task);
      setFindingsEditing(false);
      load();
    } catch { toast.error("Network error"); }
    finally { setSavingFindings(false); }
  }, [findingsDraft, opened]);

  const attachFile = useCallback(async (fileObj) => {
    if (!fileObj) return;
    if (taskClient && taskClient.has_client === false) { toast.error("No client linked — a file can't be filed."); return; }
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const r = await fetch(`${API}/analyst-tasks/${opened.task_id}/attachments`, {
        method: "POST", credentials: "include", body: fd,
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(b.detail === "no_client_linked" ? "No client linked — a file can't be filed." : (b.detail || "Upload failed"));
        return;
      }
      toast.success(`Attached "${b.attachment?.filename}" — also on ${b.client_name || "the client"}`);
      setOpened(b.task);
      load();
    } catch { toast.error("Network error"); }
    finally { setAttaching(false); }
  }, [opened, taskClient]);


  const assignTo = useCallback(async (taskId, assigneeEmail, briefForAssignee) => {
    try {
      const body = { assignee_email: assigneeEmail };
      if (briefForAssignee !== undefined && briefForAssignee !== null) {
        body.brief_for_assignee = briefForAssignee;
      }
      const r = await fetch(`${API}/analyst-tasks/${taskId}/assign`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        toast.error(b.detail || "Failed to assign");
        return null;
      }
      const d = await r.json();
      toast.success(assigneeEmail === user?.email ? "Assigned to you" : `Assigned to ${assigneeEmail.split("@")[0]}`);
      return d.task;
    } catch {
      toast.error("Network error");
      return null;
    }
  }, [user]);

  const saveBrief = useCallback(async (taskId, brief) => {    try {
      const r = await fetch(`${API}/analyst-tasks/${taskId}/brief`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brief_for_assignee: brief }),
      });
      if (!r.ok) {
        toast.error("Failed to save brief");
        return null;
      }
      const d = await r.json();
      toast.success("Brief updated");
      return d.task;
    } catch {
      toast.error("Network error");
      return null;
    }
  }, []);

  // REX-SOFT-DELETE (Jun 2026) — soft-delete a Lens card. Recoverable by
  // an admin from the DB / audit. Available to anyone who can work the
  // queue (same open model as moving a card to Approved). Confirms first.
  const deleteTask = useCallback(async (taskId) => {
    if (!window.confirm("Delete this card? It will be removed from the board (an admin can restore it).")) return false;
    try {
      const r = await fetch(`${API}/analyst-tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        toast.error(b.detail || "Failed to delete");
        return false;
      }
      toast.success("Card deleted");
      return true;
    } catch {
      toast.error("Network error");
      return false;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/analyst-tasks?limit=500`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setTasks(d.tasks || []);
      } else {
        toast.error("Couldn't load Lens tasks");
      }
    } catch (e) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "mine") return tasks.filter(t => t.assignee_email === user?.email);
    if (filter === "breached") return tasks.filter(t => t.sla_breached);
    return tasks;
  }, [tasks, filter, user]);

  const byLane = useMemo(() => {
    const out = Object.fromEntries(LANES.map(l => [l.id, []]));
    for (const t of filtered) (out[t.lane] || (out[t.lane] = [])).push(t);
    return out;
  }, [filtered]);

  const totals = useMemo(() => ({
    total: tasks.length,
    breached: tasks.filter(t => t.sla_breached && !["completed", "rejected"].includes(t.lane)).length,
    mine: tasks.filter(t => t.assignee_email === user?.email).length,
  }), [tasks, user]);

  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };

  const onDrop = (laneId) => async (e) => {
    e.preventDefault();
    const id = dragId || (e.dataTransfer && e.dataTransfer.getData("text/plain"));
    if (!id) return;
    const task = tasks.find(t => t.task_id === id);
    setDragId(null);
    if (!task || task.lane === laneId) return;
    setBusy(true);
    setTasks(prev => prev.map(t => t.task_id === id ? { ...t, lane: laneId } : t)); // optimistic
    try {
      const r = await fetch(`${API}/analyst-tasks/${id}/transition`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lane: laneId }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        toast.error(b.detail || "Move failed");
        await load();
      } else {
        const d = await r.json();
        const updated = d.task;
        setTasks(prev => prev.map(t => t.task_id === id ? { ...t, ...updated } : t));
        if (laneId === "approved" && updated.lane === "review") {
          toast.warning(`Auto-fire failed — rolled back to Review. ${updated.error || ""}`, { duration: 8000 });
        } else if (laneId === "approved" && updated.lane === "approved") {
          toast.success("Approved — downstream action fired");
        }
      }
    } catch {
      toast.error("Network error");
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1800px] mx-auto" data-testid="lens-page">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Telescope className="h-7 w-7 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Lens — Analyst Queue</h1>
            <p className="text-sm text-slate-500">Production line for every news triage. SLAs are working-day clock (weekends paused).</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* REX-COMPLETED-HISTORY (May 2026) — Kanban vs Completed toggle. */}
          <div className="flex bg-slate-100 rounded-md p-0.5 text-xs">
            <button
              data-testid="lens-view-kanban"
              onClick={() => setView("kanban")}
              className={`px-3 py-1.5 rounded ${view === "kanban" ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-600"}`}
            >Kanban</button>
            <button
              data-testid="lens-view-completed"
              onClick={() => setView("completed")}
              className={`px-3 py-1.5 rounded ${view === "completed" ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-600"}`}
            >Completed</button>
            <button
              data-testid="lens-view-insights"
              onClick={() => setView("insights")}
              className={`px-3 py-1.5 rounded ${view === "insights" ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-600"}`}
            >Insights</button>
          </div>
          {view === "kanban" && (
            <div className="flex bg-slate-100 rounded-md p-0.5 text-xs">
              {[
                { id: "all", label: `All (${totals.total})` },
                { id: "mine", label: `Mine (${totals.mine})` },
                { id: "breached", label: `Breached (${totals.breached})` },
              ].map(c => (
                <button key={c.id}
                  data-testid={`lens-filter-${c.id}`}
                  onClick={() => setFilter(c.id)}
                  className={`px-3 py-1.5 rounded ${filter === c.id ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-600"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="lens-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {view === "completed" ? (
        <LensCompletedTab user={user} onOpen={(t) => setOpened(t)} />
      ) : view === "insights" ? (
        <LensInsights />
      ) : loading && tasks.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-slate-400"><Loader2 className="animate-spin h-6 w-6" /></div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {LANES.map(lane => (
            <div
              key={lane.id}
              onDragOver={e => e.preventDefault()}
              onDrop={onDrop(lane.id)}
              className={`flex-shrink-0 w-[270px] rounded-lg border ${lane.tint} flex flex-col`}
              data-testid={`lens-lane-${lane.id}`}
            >
              <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg ${lane.header}`}>
                <span className="text-xs font-bold uppercase tracking-wide">{lane.label}</span>
                <Badge variant="outline" className="bg-white/70 text-[10px]" data-testid={`lens-lane-count-${lane.id}`}>
                  {(byLane[lane.id] || []).length}
                </Badge>
              </div>
              <div className="p-2 flex-1 min-h-[200px]">
                {(byLane[lane.id] || []).map(t => (
                  <CardChip key={t.task_id} task={t} onDragStart={onDragStart(t.task_id)} onOpen={() => setOpened(t)} />
                ))}
                {(byLane[lane.id] || []).length === 0 && (
                  <p className="text-[11px] text-slate-400 text-center py-6 italic">empty</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task detail dialog */}
      <Dialog open={!!opened} onOpenChange={() => setOpened(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lens-task-dialog">
          {opened && (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">{opened.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <Badge className={`border ${SLA_TONE[opened.sla_label] || SLA_TONE.unknown}`}>
                    {opened.sla_breached ? "BREACHED " : ""}{fmtRemaining(opened.sla_hours_remaining)} remaining
                  </Badge>
                  <span className="text-xs text-slate-500">SLA {opened.sla_hours}h · {TRIAGE_LABELS[opened.triage_action] || opened.triage_action}</span>
                </div>
                {opened.error && (
                  <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-800">
                    <strong>Auto-fire failed:</strong> {opened.error}
                  </div>
                )}
                {opened.close_outcome && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs" data-testid="lens-close-record">
                    <p className="font-semibold text-emerald-800 flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Closed — {opened.close_outcome_label || opened.close_outcome}
                    </p>
                    {opened.close_note && <p className="text-emerald-900 mt-1 whitespace-pre-wrap">{opened.close_note}</p>}
                    {opened.close_other_text && <p className="text-emerald-900 mt-1">“{opened.close_other_text}”</p>}
                    <p className="text-emerald-700 mt-1">
                      {opened.close_client_landed ? "Note saved to the client." : "Not linked to a client."}
                      {opened.close_review_date ? ` · Review ${opened.close_review_date}` : ""}
                      {opened.closed_by ? ` · by ${opened.closed_by.split("@")[0]}` : ""}
                    </p>
                  </div>
                )}
                {/* REX-BRIEF (May 2026) — angle / note from the assigner.
                    Anyone with card access can edit it inline. */}
                {(opened.brief_for_assignee || briefEditing) && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3" data-testid="lens-brief-block">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-800">Brief for Assignee</p>
                      {!briefEditing && (
                        <button
                          onClick={() => {
                            setBriefDraft(opened.brief_for_assignee || "");
                            setBriefEditing(true);
                          }}
                          className="text-[10px] text-amber-800 hover:text-amber-900 flex items-center gap-1"
                          aria-label="Edit brief for assignee"
                          data-testid="lens-brief-edit"
                        >
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                      )}
                    </div>
                    {briefEditing ? (
                      <div className="space-y-2">
                        <textarea
                          value={briefDraft}
                          onChange={(e) => setBriefDraft(e.target.value)}
                          rows={4}
                          className="w-full text-sm border border-amber-300 rounded p-2 bg-white focus:ring-1 focus:ring-amber-400 outline-none"
                          placeholder="What's the angle? Tone, format, what to avoid…"
                          data-testid="lens-brief-textarea"
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => { setBriefEditing(false); setBriefDraft(""); }}
                            data-testid="lens-brief-cancel"
                          >Cancel</Button>
                          <Button
                            size="sm"
                            onClick={async () => {
                              const updated = await saveBrief(opened.task_id, briefDraft);
                              if (updated) {
                                setOpened({ ...opened, brief_for_assignee: updated.brief_for_assignee });
                                setBriefEditing(false);
                                load();
                              }
                            }}
                            data-testid="lens-brief-save"
                          >Save brief</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-amber-900 whitespace-pre-wrap">{opened.brief_for_assignee}</p>
                    )}
                  </div>
                )}
                {!opened.brief_for_assignee && !briefEditing && (
                  <button
                    onClick={() => { setBriefDraft(""); setBriefEditing(true); }}
                    className="text-xs text-blue-700 hover:underline flex items-center gap-1"
                    data-testid="lens-brief-add"
                  >
                    <Pencil className="h-3 w-3" /> Add a brief for the assignee
                  </button>
                )}
                {/* G2 — Analyst output: findings + attachments, readable in the scrum. */}
                <div className="bg-cyan-50 border border-cyan-200 rounded p-3" data-testid="lens-output-block">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-cyan-800">Analyst Output</p>
                    {opened.findings && !findingsEditing && (
                      <button
                        onClick={() => { setFindingsDraft(opened.findings || ""); setFindingsEditing(true); }}
                        className="text-[10px] text-cyan-800 hover:text-cyan-900 flex items-center gap-1"
                        data-testid="lens-findings-edit"
                      ><Pencil className="h-3 w-3" /> Edit</button>
                    )}
                  </div>
                  {opened.findings && !findingsEditing ? (
                    <div data-testid="lens-findings-display">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{opened.findings}</p>
                      <p className="text-[11px] text-cyan-700 mt-1">
                        {opened.findings_by_name || opened.findings_by || "—"}
                        {opened.findings_at ? ` · ${new Date(opened.findings_at).toLocaleString()}` : ""}
                        {opened.findings_client_landed ? " · filed on the client" : (taskClient && taskClient.has_client === false ? " · no client linked" : "")}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <textarea
                        value={findingsDraft}
                        onChange={(e) => setFindingsDraft(e.target.value)}
                        rows={5}
                        className="w-full text-sm border border-cyan-300 rounded p-2 bg-white focus:ring-1 focus:ring-cyan-400 outline-none"
                        placeholder="What did you find? Write it in your own words, at whatever length you need…"
                        data-testid="lens-findings-textarea"
                      />
                      <div className="flex gap-2 justify-end">
                        {opened.findings && (
                          <Button variant="outline" size="sm" onClick={() => { setFindingsEditing(false); setFindingsDraft(opened.findings || ""); }} data-testid="lens-findings-cancel">Cancel</Button>
                        )}
                        <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" disabled={savingFindings || !findingsDraft.trim()} onClick={saveFindings} data-testid="lens-findings-save">
                          {savingFindings ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save findings"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Attachments */}
                  <div className="mt-3 border-t border-cyan-200 pt-2">
                    {(opened.attachments?.length ?? 0) > 0 && (
                      <ul className="space-y-1 mb-2" data-testid="lens-attachments">
                        {opened.attachments.map((a, i) => (
                          <li key={a.document_id || i} className="flex items-center justify-between gap-2 text-xs">
                            <a
                              href={`${API}/client-documents/download/${a.document_id}`}
                              target="_blank" rel="noreferrer"
                              className="text-cyan-800 underline truncate"
                              data-testid={`lens-attachment-${i}`}
                            >{a.filename}</a>
                            <span className="text-cyan-600 whitespace-nowrap">{a.uploaded_by_name ? a.uploaded_by_name.split("@")[0] : ""}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {taskClient && taskClient.has_client === false ? (
                      <p className="text-[11px] text-slate-500" data-testid="lens-attach-no-client">No client linked to this task — a file can't be filed, so nothing is attached.</p>
                    ) : (
                      <label className="inline-flex items-center gap-2 text-xs text-cyan-800 cursor-pointer hover:text-cyan-900" data-testid="lens-attach-label">
                        {attaching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3 w-3" />}
                        {attaching ? "Uploading…" : "Attach a file"}
                        <input type="file" className="hidden" disabled={attaching} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; attachFile(f); }} data-testid="lens-attach-input" />
                      </label>
                    )}
                  </div>
                </div>
                {opened.rubric?.length > 0 && (
                  <div className="bg-slate-50 rounded p-3">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Rubric</p>
                    <ul className="list-disc pl-4 space-y-1 text-slate-700">
                      {opened.rubric.map((r, i) => <li key={i} className="text-sm">{r}</li>)}
                    </ul>
                  </div>
                )}
                {opened.source_payload && Object.keys(opened.source_payload).length > 0 && (
                  <div className="bg-white border rounded p-3">
                    <p className="text-xs font-semibold text-slate-500 mb-1">Source</p>
                    {opened.source_payload.headline && <p className="font-medium text-slate-800">{opened.source_payload.headline}</p>}
                    {opened.source_payload.company && <p className="text-xs text-slate-500">Company: {opened.source_payload.company}</p>}
                    {opened.source_payload.country && <p className="text-xs text-slate-500">Country: {opened.source_payload.country}</p>}
                    {opened.source_payload.source_url && (
                      <a href={opened.source_payload.source_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">Read source</a>
                    )}
                    {opened.source_payload.campaign_id && (
                      <div className="mt-1 text-xs text-slate-600" data-testid="lens-campaign-source">
                        {opened.source_payload.campaign_name && <p>Campaign: <span className="font-medium">{opened.source_payload.campaign_name}</span></p>}
                        {opened.source_payload.sent_count != null && <p>{opened.source_payload.sent_count} email(s) sent{opened.source_payload.sender ? ` by ${opened.source_payload.sender}` : ""}</p>}
                        <a href="/campaigns?tab=history" className="text-blue-600 underline" data-testid="lens-open-campaign">Open in Campaigns</a>
                      </div>
                    )}
                    {opened.source_payload.reference && Array.isArray(opened.source_payload.candidates) && (
                      <div className="mt-1 text-xs text-slate-600" data-testid="lens-assign-client-source">
                        <p>Unmatched name: <span className="font-medium">{opened.source_payload.reference}</span></p>
                        {opened.source_payload.context && <p className="text-slate-500">{opened.source_payload.context}</p>}
                        <p>Candidates: {opened.source_payload.candidates.length ? opened.source_payload.candidates.map(c => c.company_name).join(", ") : "none"}</p>
                        <a href="/clients" className="text-blue-600 underline">Open Clients</a>
                      </div>
                    )}
                  </div>
                )}
                <div className="text-xs text-slate-500 grid grid-cols-2 gap-2">
                  <div>Created by: {opened.created_by_email}</div>
                  <div>Assignee: {opened.assignee_email || "—"}</div>
                  <div>Lane: {LANES.find(l => l.id === opened.lane)?.label || opened.lane}</div>
                  <div>Due: {new Date(opened.sla_due_at).toLocaleString()}</div>
                </div>
                {/* Lane history — appended on every transition (May 2026). */}
                <div className="bg-white border rounded p-3" data-testid="lens-lane-history">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Lane history</p>
                  {(opened.lane_history?.length ?? 0) === 0 ? (
                    <p className="text-xs text-slate-500">
                      No transitions yet — this task is still in {LANES.find(l => l.id === opened.lane)?.label || opened.lane}.
                    </p>
                  ) : (
                    <ol className="space-y-1.5 text-xs text-slate-700">
                      {opened.lane_history.map((h, i) => {
                        const fromLbl = LANES.find(l => l.id === h.from)?.label || h.from || "—";
                        const toLbl = LANES.find(l => l.id === h.to)?.label || h.to;
                        return (
                          <li
                            key={`${h.at}-${i}`}
                            className="flex flex-wrap items-baseline gap-x-2"
                            data-testid={`lens-lane-history-entry-${i}`}
                          >
                            <span className="font-medium text-slate-800">
                              {fromLbl} → {toLbl}
                            </span>
                            <span className="text-slate-500">
                              {h.at ? new Date(h.at).toLocaleString() : ""}
                            </span>
                            <span className="text-slate-500">
                              · {h.actor_email || "—"}
                            </span>
                            {h.note && (
                              <span className="text-rose-600 italic">· {h.note}</span>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                <Button
                  variant="outline" size="sm"
                  onClick={async () => {
                    await fetch(`${API}/analyst-tasks/${opened.task_id}/snooze`, {
                      method: "POST", credentials: "include",
                      headers: {"Content-Type":"application/json"},
                      body: JSON.stringify({ days: 1 }),
                    });
                    toast.success("Snoozed 1 working day");
                    setOpened(null); load();
                  }}
                  data-testid="lens-snooze-btn"
                >Snooze 1d</Button>
                {/* Admin: open the AssignWithBriefModal to route the card with an angle.
                    Replaces the prior inline <select> (May 2026) so admins can also
                    leave a "Brief for Assignee" note in the same step. */}
                {isAdmin && analystPool.length > 0 && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setAssignModalOpen(true)}
                    data-testid="lens-assign-open-btn"
                  >
                    Assign…
                  </Button>
                )}
                {canSelfClaim && opened.assignee_email !== user?.email && (
                  <Button
                    variant="outline" size="sm"
                    onClick={async () => {
                      const updated = await assignTo(opened.task_id, user?.email);
                      if (updated) { setOpened(null); load(); }
                    }}
                    data-testid="lens-claim-btn"
                  >Claim</Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                  onClick={async () => {
                    const ok = await deleteTask(opened.task_id);
                    if (ok) { setOpened(null); load(); }
                  }}
                  data-testid="lens-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
                {!["completed", "rejected"].includes(opened.lane) && (
                  <Button
                    variant="outline" size="sm"
                    className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                    onClick={() => setGiveModalOpen(true)}
                    data-testid="lens-give-btn"
                  >
                    Assign to…
                  </Button>
                )}
                {!["completed", "rejected"].includes(opened.lane) && (
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => setCloseModalOpen(true)}
                    data-testid="lens-close-card-btn"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Close card…
                  </Button>
                )}
                <Button variant="outline" onClick={() => setOpened(null)} data-testid="lens-dismiss-btn">Dismiss</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* G2 Part 4 — reuse the H1 "Give to…" hand-off: real-staff picker, the
          findings + file travel with it, lands as a card with its three exits. */}
      <AssignWithBriefModal
        open={giveModalOpen}
        onClose={() => setGiveModalOpen(false)}
        title="Assign to…"
        subtitle={opened?.title || ""}
        assignablePool={staffPool}
        currentUserEmail={user?.email}
        confirmLabel="Assign to person"
        onConfirm={async ({ assignee_email, brief_for_assignee }) => {
          if (!opened || !assignee_email) return false;
          const links = (opened.attachments || []).map(a => `${a.filename}: ${API}/client-documents/download/${a.document_id}`).join("\n");
          const bodyParts = [];
          if (opened.findings) bodyParts.push(`Findings:\n${opened.findings}`);
          if (links) bodyParts.push(`Attached files:\n${links}`);
          if (opened.brief_for_assignee) bodyParts.push(`Original brief:\n${opened.brief_for_assignee}`);
          try {
            const r = await fetch(`${API}/scrum/v2/handoff`, {
              method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                source_kind: "lens",
                source_id: opened.task_id,
                title: opened.title || "Lens card",
                body: bodyParts.join("\n\n"),
                note: brief_for_assignee || "",
                source_url: "/lens",
                source_label: "Lens card",
                client_name: taskClient?.client_name || opened.source_payload?.client_name || opened.source_payload?.company || "",
                assignee_email,
                section: "commercial",
              }),
            });
            const b = await r.json().catch(() => ({}));
            if (!r.ok) { toast.error(b.detail || "Could not hand over"); return false; }
            const who = staffPool.find(s => s.email === assignee_email)?.name || assignee_email.split("@")[0];
            toast.success(`Given to ${who} — it's on their Today list`);
            setGiveModalOpen(false);
            return true;
          } catch { toast.error("Network error"); return false; }
        }}
      />

      {/* Assign-with-brief modal (May 2026) — replaces the bare select picker. */}
      <AssignWithBriefModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onConfirm={async ({ assignee_email, brief_for_assignee }) => {
          if (!opened) return false;
          const updated = await assignTo(opened.task_id, assignee_email, brief_for_assignee);
          if (updated) {
            setOpened(null);
            load();
            return true;
          }
          return false;
        }}
        title="Assign Lens card"
        subtitle={opened?.title || ""}
        assignablePool={analystPool}
        currentAssignee={opened?.assignee_email || ""}
        currentBrief={opened?.brief_for_assignee || ""}
        suggestedBrief={
          opened?.source_payload?.headline
            ? `Re: ${opened.source_payload.headline}. What's the angle? Tone, format, what to avoid…`
            : ""
        }
        currentUserEmail={user?.email}
        confirmLabel="Assign with brief"
      />

      {/* REX-LENS-CLOSE (Jun 2026) — structured close-with-outcome. */}
      {opened && (
        <CloseCardModal
          open={closeModalOpen}
          onClose={() => setCloseModalOpen(false)}
          task={opened}
          analystPool={analystPool}
          currentUserEmail={user?.email}
          onClosed={() => { setCloseModalOpen(false); setOpened(null); load(); }}
        />
      )}
    </div>
  );
}


// ─── REX-LENS-CLOSE INSIGHTS (Jun 2026) ───────────────────────────────
// The learning loop: what outcomes people picked when closing, and — the
// point — the verbatim "Something else" free text, so we can see the outcome
// types we failed to anticipate and build them next.
const OUTCOME_LABELS = {
  done_nothing_pending: "Done, nothing pending",
  done_awaiting_reply: "Done, awaiting a reply",
  done_handed_off: "Handed to campaign/proposal",
  needs_decision: "Needs a decision",
  not_worth_doing: "Not worth doing",
  other: "Something else",
};

function LensInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/analyst-tasks/close-outcomes/summary`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="flex items-center justify-center h-48 text-slate-400"><Loader2 className="animate-spin h-6 w-6" /></div>;
  if (!data || data.total_closed === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-slate-400" data-testid="lens-insights-empty">
        <p className="text-sm">No cards have been closed yet.</p>
        <p className="text-xs">Close outcomes and the "Something else" notes will appear here.</p>
      </div>
    );
  }
  const total = data.total_closed || 0;
  const entries = Object.entries(data.by_outcome || {}).sort((a, b) => b[1] - a[1]);
  const others = data.other_texts || [];
  return (
    <div className="space-y-6 max-w-3xl" data-testid="lens-insights">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border rounded-lg p-3"><p className="text-xs text-slate-500">Cards closed</p><p className="text-2xl font-semibold text-slate-900">{total}</p></div>
        <div className="bg-white border rounded-lg p-3"><p className="text-xs text-slate-500">Notes on a client</p><p className="text-2xl font-semibold text-emerald-700">{data.note_landed_on_client || 0}</p></div>
        <div className="bg-white border rounded-lg p-3"><p className="text-xs text-slate-500">“Something else”</p><p className="text-2xl font-semibold text-indigo-700">{others.length}</p></div>
      </div>
      <div className="bg-white border rounded-lg p-4">
        <p className="text-xs font-semibold text-slate-600 mb-3">Outcomes chosen</p>
        <div className="space-y-2">
          {entries.map(([id, n]) => (
            <div key={id} className="flex items-center gap-3" data-testid={`lens-insight-outcome-${id}`}>
              <span className="text-sm text-slate-700 w-52 shrink-0">{OUTCOME_LABELS[id] || id}</span>
              <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden">
                <div className="bg-indigo-500 h-4" style={{ width: `${total ? Math.round((n / total) * 100) : 0}%` }} />
              </div>
              <span className="text-xs text-slate-500 w-10 text-right tabular-nums">{n}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border rounded-lg p-4" data-testid="lens-insights-others">
        <p className="text-xs font-semibold text-slate-600 mb-1">What people typed into “Something else”</p>
        <p className="text-[11px] text-slate-400 mb-3">These are the outcome types we haven’t built yet.</p>
        {others.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Nobody has used the escape hatch yet.</p>
        ) : (
          <ul className="space-y-2">
            {others.map((o, i) => (
              <li key={`${o.task_id}-${i}`} className="border-l-2 border-indigo-300 pl-3" data-testid={`lens-insight-other-${i}`}>
                <p className="text-sm text-slate-800">“{o.text}”</p>
                <p className="text-[11px] text-slate-400">{o.title ? `${o.title} · ` : ""}{o.closed_by ? o.closed_by.split("@")[0] : ""}{o.closed_at ? ` · ${o.closed_at.slice(0, 10)}` : ""}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


// ─── REX-COMPLETED-HISTORY (May 2026) ─────────────────────────────────
// LensCompletedTab — "what shipped or got killed over time" archive view.
// Backed by the shared <CompletedHistoryPanel> + Recharts sparkline.
function LensCompletedTab({ user, onOpen }) {
  const [assignee, setAssignee] = useState("");
  const [titleQuery, setTitleQuery] = useState("");
  const [mineOnly, setMineOnly] = useState(false);

  const fetchRows = async ({ from, to, lanes, signal }) => {
    if (lanes.length === 0) return [];
    const params = new URLSearchParams({
      lanes: lanes.join(","),
      from,
      to,
      limit: "500",
    });
    if (mineOnly) params.set("mine", "true");
    else if (assignee) params.set("assignee_email", assignee);
    const r = await fetch(`${API}/analyst-tasks?${params.toString()}`, { credentials: "include", signal });
    const d = await r.json().catch(() => ({ tasks: [] }));
    let list = d.tasks || [];
    if (titleQuery) {
      const q = titleQuery.toLowerCase();
      list = list.filter((t) =>
        (t.title || "").toLowerCase().includes(q)
        || (t.source_payload?.headline || "").toLowerCase().includes(q)
        || (t.source_payload?.company || "").toLowerCase().includes(q)
      );
    }
    return list;
  };

  return (
    <CompletedHistoryPanel
      testidPrefix="lens-completed"
      sparklineLabel="completed-per-day"
      defaultRangeKey="30d"
      enabledRanges={["today", "7d", "30d", "90d", "qtd", "ytd"]}
      lanesConfig={[
        { id: "completed", label: "Completed" },
        { id: "rejected",  label: "Rejected"  },
      ]}
      defaultLanes={["completed", "rejected"]}
      lanesLabel="Outcome"
      extrasKey={`${mineOnly}|${assignee}|${titleQuery}`}
      renderExtraFilters={() => (
        <>
          <div className="space-y-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Search title / company</label>
            <input
              type="text" value={titleQuery}
              onChange={(e) => setTitleQuery(e.target.value)}
              placeholder="e.g. CBN, Acme"
              className="h-8 px-2 text-xs rounded border border-slate-300 bg-white w-[180px]"
              data-testid="lens-completed-search"
            />
          </div>
          <div className="space-y-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Assignee email</label>
            <input
              type="text" value={assignee}
              onChange={(e) => { setAssignee(e.target.value); setMineOnly(false); }}
              placeholder="optional"
              disabled={mineOnly}
              className="h-8 px-2 text-xs rounded border border-slate-300 bg-white w-[180px] disabled:bg-slate-50"
              data-testid="lens-completed-assignee"
            />
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
              data-testid="lens-completed-mine"
            />
            Mine only
          </label>
        </>
      )}
      countsBuilder={(rows) => {
        const m = { total: rows.length, completed: 0, rejected: 0 };
        rows.forEach((t) => {
          if (t.lane === "completed") m.completed += 1;
          if (t.lane === "rejected") m.rejected += 1;
        });
        return m;
      }}
      summaryRender={(counts, from, to, loading) => loading ? "Loading…" : (
        <>
          Showing <span className="font-bold">{counts.total}</span> item{counts.total === 1 ? "" : "s"} between <span className="font-medium">{from}</span> and <span className="font-medium">{to}</span>
          {counts.completed ? <> · {counts.completed} completed</> : null}
          {counts.rejected ? <> · {counts.rejected} rejected</> : null}
        </>
      )}
      columns={[
        { key: "title", label: "Title",
          render: (t) => <span className="font-medium text-slate-900 truncate max-w-[280px] block">{t.title || "(untitled)"}</span>,
          csv: (t) => t.title || "" },
        { key: "outcome", label: "Outcome",
          render: (t) => <Badge variant="outline" className={`text-[10px] ${t.lane === "completed" ? "border-emerald-300 text-emerald-700" : "border-rose-300 text-rose-700"}`}>{t.lane}</Badge>,
          csv: (t) => t.lane || "" },
        { key: "assignee", label: "Assignee", className: "text-slate-600",
          render: (t) => t.assignee_email ? t.assignee_email.split("@")[0] : "—",
          csv: (t) => t.assignee_email || "" },
        { key: "action", label: "Action", className: "text-slate-600",
          render: (t) => t.triage_action || "—",
          csv: (t) => t.triage_action || "" },
        { key: "completed", label: "Completed", className: "text-slate-500 tabular-nums text-xs",
          render: (t) => (t.completed_at || t.updated_at || "").slice(0, 16).replace("T", " "),
          csv: (t) => (t.completed_at || t.updated_at || "").slice(0, 19).replace("T", " ") },
      ]}
      csvHeader={["Title", "Outcome", "Assignee", "Triage action", "Completed at", "Source headline", "Company"]}
      csvExtraCells={(t) => [t.source_payload?.headline || "", t.source_payload?.company || ""]}
      csvFilename={(from, to) => `lens-completed_${from}_to_${to}.csv`}
      rowId={(t) => t.task_id}
      rowDateFor={(t) => t.completed_at || t.updated_at}
      onRowClick={(t) => onOpen && onOpen(t)}
      fetchRows={fetchRows}
      emptyMessage="No completed items in this date range. Adjust the range or filters above."
    />
  );
}
